<template>
 
  <PageHero
    v-if="property && !property.unpublished"
    :title="property.title"
    :backgroundImageUrl="property.cover_image_url"
    :loaded="heroLoaded"
    :breadcrumbsTop="[
      { label: $t('hero.breadcrumbs.home'), link: '/' },
      { label: $t('hero.breadcrumbs.properties'), link: '/public/properties' },
      ...(property.category_label ? [{ label: property.category_label }] : []),
      ...(property.category_type_label ? [{ label: property.category_type_label }] : []),
      ...(property.category_subtype_label ? [{ label: property.category_subtype_label }] : [])
    ]"
    :breadcrumbsBottom="[
      ...(property.country ? [{ label: property.country }] : []),
      ...(property.region ? [{ label: property.region }] : []),
      ...(property.city ? [{ label: property.city }] : []),
      ...(property.district ? [{ label: property.district }] : [])
    ]"
  /> 
  <section class="module ptb-100">
    <div class="container">
      <div class="row">
        <!-- Левая колонка -->
        <div class="col-lg-4 col-md-4" v-if="property && !property.unpublished">
          <div class="sidebar-left pr-4">
            <ContactSeller
              :agent-fullname="property.agent_fullname"
              :agency="property.agency"
              :telefon="property.telefon"
              :agent-photo="property.agent_photo"
              :agency-id="property.agency_id"
            />
            <PropertyPriceRange
              v-if="property.evaluation_predictions"
              :price="Number(property.price)"
              :medianPrice="Number(property.evaluation_predictions.median_price)"
              :averagePrice="Number(property.evaluation_predictions.average_price)"
            />

            <AiEvaluationBar
              v-if="property.evaluation_predictions"
              :predictions="property.evaluation_predictions"
            />

          
            <div class="text-center my-3">
              <button
                class="btn secondary-solid-btn btn-sm"
                style="background-color: #2563eb;"
                @click="showLeadForm"
              >
                📩 {{ $t('property_detail.property.request_price') }}
              </button>
            </div>
            
            <PropertyPredictionChart
              v-if="property.evaluation_predictions"
              :predictions="property.evaluation_predictions"
            />
            <PropertyCover :coverImageUrl="property.cover_image_url" :weight="'200px'" />
            <PropertyDetailsSidebar :property="property" />
            <PropertyLocation :property="property" />
            <PropertyFeatures :property="property" />
            <PropertyVideo v-if="property?.video_url" :video-url="property.video_url" />
          </div>
        </div>

        <!-- Правая колонка -->
        <div class="col-lg-8 col-md-8">
          <!-- Заглушка, если объект снят с публикации -->
          <div v-if="property?.unpublished" class="text-center mt-10">
            <h2 class="text-danger mb-4">{{ $t('property_detail.property.unpublished.title') }} </h2>
            <p class="mb-3"> {{ $t('property_detail.property.unpublished.message') }}.</p>
            <a href="/" class="btn btn-primary mt-4">{{ $t('property_detail.property.unpublished.home') }} </a>
          </div>

          <!-- Страница объекта -->
          <div v-else-if="property">
            <PropertyInfo :property="property" />
            <PropertyMap v-if="property.latitude && property.longitude" :latitude="property.latitude" :longitude="property.longitude" />
            <PropertyGallery :galleryImages="property.gallery_images" />
            <BackButton />
          </div>

          <!-- Идёт загрузка -->
          <div v-else class="text-center text-gray-500 mt-10">
            {{ $t('property.loading') }}
          </div>


          <!-- Модальное окно формы -->
          <RequestPriceModal
            v-if="property"
            :visible="leadFormVisible"
            :propertyId="property.id"
            @close="closeLeadForm"
          />

        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import { ref, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/entrypoints/public/plugins/axios'
import { useSeo } from '@/entrypoints/public/composables/useSeo'

import PropertyCover from '../pages/property_detail/PropertyCover.vue'
import PropertyInfo from '../pages/property_detail/PropertyInfo.vue'
import PropertyGallery from '../pages/property_detail/PropertyGallery.vue'
import BackButton from '../pages/property_detail/BackButton.vue'
import PropertyFeatures from '../pages/property_detail/PropertyFeatures.vue'
import PropertyLocation from '../pages/property_detail/PropertyLocation.vue'
import PropertyDetailsSidebar from '../pages/property_detail/PropertyDetailsSidebar.vue'
import PropertyMap from '../pages/PropertyMap.vue'
import ContactSeller from '../pages/property_detail/ContactSeller.vue'
import PropertyVideo from '../pages/property_detail/PropertyVideo.vue'
import RequestPriceModal from '../pages/RequestPriceModal.vue'
import AiEvaluationBar from '../pages/property_detail/AiEvaluationBar.vue'
import PropertyAiExplanation from '../pages/property_detail/PropertyAiExplanation.vue'

import PageHero from '../components/PageHero.vue'
import PropertyPriceRange from '../components/PropertyPriceRange.vue'

const { locale } = useI18n()
const { setDynamicSeo } = useSeo()   // ✅ обязательно
const property = ref(null)
const leadFormVisible = ref(false)

const showLeadForm = () => (leadFormVisible.value = true)
const closeLeadForm = () => (leadFormVisible.value = false)

const loadProperty = async () => {
  try {
    const pathParts = window.location.pathname.split('/')
    const id = pathParts[pathParts.length - 1]

    const response = await api.get(`/properties/${id}`, {
      headers: { 'X-Locale': locale.value }
    })

    property.value = response.data

    await nextTick()

    if (property.value) {
      setDynamicSeo({
        title:
          property.value.meta_title ||
          `${property.value.title} — ${property.value.city || ''} — Estimator24`,
        description:
          property.value.meta_description ||
          `Квартира ${property.value.rooms || ''}, ${property.value.net_area || ''} м² в ${
            property.value.city || ''
          }. Цена: ${property.value.price ? property.value.price + ' €' : '—'}.`,
        canonical: property.value.canonical_url,
        image: property.value.cover_image_url
      })
    }
  } catch (error) {
    console.error('Ошибка загрузки квартиры:', error)
  }
}

const heroLoaded = ref(false)

watch(property, (val) => {
  if (!val?.cover_image_url) return

  const img = new Image()
  img.src = val.cover_image_url
  img.onload = () => {
    heroLoaded.value = true
  }
})

onMounted(() => loadProperty())
watch(locale, () => loadProperty())
</script>


<style>

.container {
  padding-left: 10px;
  padding-right: 10px;
}

@media (min-width: 1200px) {
  .col-lg-8 {
    width: 75%;
  }
  .col-lg-4 {
    width: 25%;
  }
}

</style>